// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import {
  handleApiCall,
  buildParams,
  calculateSkip,
  cachedGet,
} from "../utils/apiHelper";

export const bookingService = {
  /**
   * Get all bookings with filters
   */
  getBookings: async (filters = {}) => {
    const params = buildParams({
      skip: calculateSkip(filters.page, filters.limit),
      limit: filters.limit || 20,
      status: filters.status || "active",
      eventType: filters.eventType,
      search: filters.search,
      dateFilter: filters.dateFilter,
      tenantId: filters.tenantId,
      startDate: filters.startDate,
      endDate: filters.endDate,
      llmLabel: filters.llmLabel,
      problematic: filters.problematic,
      zur_pruefung: filters.zur_pruefung,
      userId: filters.userId,
    });

    return handleApiCall(
      () => apiClient.get("/bookings/admin/bookings", { params }),
      "Error fetching bookings",
    );
  },

  /**
   * Get user's own bookings
   */
  getUserBookings: async (userId, filters = {}) => {
    const params = buildParams({
      skip: calculateSkip(filters.page, filters.limit),
      limit: filters.limit || 20,
      status: filters.status,
      search: filters.search,
    });

    return handleApiCall(
      () => apiClient.get(`/bookings/${userId}`, { params }),
      "Error fetching user bookings",
    );
  },

  /**
   * Update booking status
   */
  updateBookingStatus: async (bookingId, newStatus) => {
    return handleApiCall(
      () =>
        apiClient.put(`/bookings/${bookingId}/status`, { status: newStatus }),
      "Error updating booking status",
    );
  },

  /**
   * Get booking details by ID
   */
  getBookingDetails: async (bookingId) => {
    return handleApiCall(
      () => apiClient.get(`/bookings/admin/booking/${bookingId}`),
      "Error fetching booking details",
    );
  },

  /**
   * Delete a booking
   */
  deleteBooking: async (bookingId) => {
    return handleApiCall(
      () => apiClient.delete(`/bookings/admin/booking/${bookingId}`),
      "Error deleting booking",
    );
  },

  /**
   * Bulk delete bookings
   */
  bulkDeleteBookings: async (bookingIds) => {
    return handleApiCall(
      () =>
        apiClient.post(`/bookings/admin/bookings/bulk-delete`, {
          booking_ids: bookingIds,
        }),
      "Error bulk deleting bookings",
    );
  },

  /**
   * Get booking statistics. Accepts any filter keys (camelCase or snake_case);
   * undefined/null/empty values are stripped. Backend sees only keys you set.
   */
  getBookingStats: async (filters = {}) =>
    handleApiCall(
      () => cachedGet("/bookings/stats", { params: buildParams(filters) }),
      "Error fetching booking stats",
    ),

  /**
   * Get booking trends
   */
  getBookingTrends: async (periodType = "daily", dateRange) => {
    const params = buildParams({
      period_type: periodType,
      start_date: dateRange?.start,
      end_date: dateRange?.end,
    });

    return handleApiCall(
      () => cachedGet("/metrics/bookings/trends", { params }),
      "Error fetching booking trends",
    );
  },

  /**
   * Get the unified recent-activity feed (bookings, payments, system/LLM).
   * Each item carries a `category` (bookings | payments | system).
   */
  getRecentActivity: async (limit = 12, tenantId, days = 7) => {
    const params = buildParams({ limit, tenant_id: tenantId, days });

    return handleApiCall(
      () => apiClient.get("/metrics/bookings/recent-activity", { params }),
      "Error fetching recent activity",
    );
  },

  /**
   * Get booking summary with trends
   */
  getBookingSummary: async (
    includeTrends = true,
    comparisonPeriod = "7d",
    dateRange,
  ) => {
    const params = buildParams({
      include_trends: includeTrends,
      start_date: comparisonPeriod === "custom" ? dateRange?.start : undefined,
      end_date: comparisonPeriod === "custom" ? dateRange?.end : undefined,
      comparison_period:
        comparisonPeriod !== "custom" ? comparisonPeriod : undefined,
    });

    return handleApiCall(
      () => cachedGet("/metrics/bookings/summary", { params }),
      "Error fetching booking summary",
    );
  },
};
